import { useState, useEffect, useCallback } from "react";
import { useTellescopeQueryClient } from "@tellescope/tanstack-query";
import { useEnduserStore } from "../store/hooks";
import { evaluate_conditional_logic_for_enduser_fields } from "@tellescope/utilities";
import type { Form, FormResponse } from "@tellescope/types-models";

export interface PinnedFormDisplay {
  form: Form;
  /** Whether there's an outstanding (unsubmitted) form response */
  hasOutstanding: boolean;
  /** The outstanding form response if any */
  outstandingResponse: FormResponse | null;
  /** The most recently submitted form response if any */
  latestSubmitted: FormResponse | null;
}

/**
 * Hook for fetching forms available for portal submission.
 *
 * Fetches:
 * 1. All forms with allowPortalSubmission: true
 * 2. Form responses for the current enduser to determine outstanding/submitted status
 *
 * Returns forms sorted by title.
 */
export function useEnduserPinnedForms() {
  const session = useEnduserStore((state) => state.session);
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);
  const queryClient = useTellescopeQueryClient(session ?? undefined);

  const [forms, setForms] = useState<PinnedFormDisplay[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!session) {
      setForms([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch all forms with allowPortalSubmission using query client for caching
      const formsResult = await queryClient.fetchInfiniteQuery({
        model: "forms",
        method: "getSome",
        request: {
          filter: { allowPortalSubmission: true },
        },
        initialPageParam: undefined,
        getNextPageParam: (forms) => forms[forms.length - 1]?.id,
      });
      const allForms = formsResult.pages.flat() ?? [];

      // Filter forms based on enduser condition if specified
      const eligibleForms = currentEnduser
        ? allForms.filter((form) => {
            // If no condition is specified, include the form
            if (!form.allowPortalSubmissionEnduserCondition) return true;

            // Evaluate the condition against the current enduser
            try {
              return evaluate_conditional_logic_for_enduser_fields(
                currentEnduser,
                form.allowPortalSubmissionEnduserCondition
              );
            } catch (err) {
              // If condition evaluation fails, log and exclude the form
              console.warn(`Failed to evaluate condition for form ${form.id}:`, err);
              return false;
            }
          })
        : allForms;

      // Fetch form responses for the current enduser using query client for caching
      const responsesResult = await queryClient.fetchInfiniteQuery({
        model: "form_responses",
        method: "getSome",
        request: {
          filter: { formId: { _in: eligibleForms.map((f) => f.id) } },
        },
        initialPageParam: undefined,
        getNextPageParam: (responses) => responses[responses.length - 1]?.id,
      });
      const allResponses = responsesResult.pages.flat() ?? [];

      // Get form IDs for filtering responses
      const formIds = eligibleForms.map((f) => f.id);

      // Filter form responses to visible ones for this enduser
      const enduserId = currentEnduser?.id;
      const visibleResponses = allResponses.filter((f) => {
        // Not manually hidden from enduser portal
        if (f.hideFromEnduserPortal) return false;
        // Not an unsubmitted internal note
        if (!f.submittedAt && !!f.isInternalNote) return false;
        // Must be related to one of our forms
        if (!formIds.includes(f.formId)) return false;
        // Must be submitted by this enduser or outstanding
        const submittedByEnduser = f.submittedBy === enduserId;
        const isOutstanding = !f.submittedBy && !f.draftSavedAt;
        return submittedByEnduser || isOutstanding;
      });

      // Build display data for each form
      const displayForms = eligibleForms
        .map((form) => {
          const responses = visibleResponses.filter((r) => r.formId === form.id);
          const outstandingResponse = responses.find((r) => !r.submittedAt) ?? null;
          const submittedResponses = responses
            .filter((r) => !!r.submittedAt)
            .sort((a, b) => new Date(b.submittedAt!).getTime() - new Date(a.submittedAt!).getTime());
          const latestSubmitted = submittedResponses[0] ?? null;

          return {
            form,
            hasOutstanding: !!outstandingResponse,
            outstandingResponse,
            latestSubmitted,
          } satisfies PinnedFormDisplay;
        })
        // Sort by form title
        .sort((a, b) => {
          const titleA = a.form.displayTitle || a.form.title || "";
          const titleB = b.form.displayTitle || b.form.title || "";
          return titleA.localeCompare(titleB);
        });

      setForms(displayForms);
    } catch (err) {
      console.error("Failed to fetch pinned forms:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch forms"));
    } finally {
      setIsLoading(false);
    }
  }, [session, currentEnduser?.id, queryClient?.fetchInfiniteQuery]);

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    forms,
    isLoading,
    error,
    refetch: fetchData,
  };
}
